import React, { useState, useEffect, useContext } from "react";
import {
  Plus,
  Trash2,
  X,
  FileText,
  Printer,
  Save,
  Download,
  ShoppingCart,
  Truck,
  CreditCard,
  Loader2,
  Eye,
  Edit3,
  Calculator,
  Package,
  Wrench,
  User,
  Search,
  BookOpen,
} from "lucide-react";
import api from "../api/axiosInstance";
import toast, { Toaster } from "react-hot-toast";
import { AuthContext } from "../App";
const QuotationBOQ = () => {
  const { user } = useContext(AuthContext);
  const [view, setView] = useState("list"); // 'list' or 'create'
  const [quotations, setQuotations] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saveLoading, setSaveLoading] = useState(false);
  const [viewingQuote, setViewingQuote] = useState(null);

  const initialBOQ = {
    id: null,
    client_name: "",
    quotation_no: "",
    items: [
      {
        id: Date.now(),
        description: "",
        qty: 1,
        mat_rate: 0,
        lab_rate: 0,
        margin: 15,
        total: 0,
      },
    ],
    overall_transport: 0,
    overall_material_extra: 0,
    discount: 0,
    gst_rate: 18,
    terms_conditions:
      "1. Validity: 15 days.\n2. Payment: 50% Advance, 40% Delivery, 10% Completion.",
  };

  const [boq, setBoq] = useState(initialBOQ);

  useEffect(() => {
    loadInitialData();
  }, []);

  const loadInitialData = async () => {
    try {
      const qtnRes = await api.get("get_quotations.php");
      setQuotations(qtnRes.data.quotations || []);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  const fetchNextQTN = async () => {
    try {
      const res = await api.get("get_next_quotation_no.php");
      if (res.data.status === "success") {
        setBoq((prev) => ({ ...prev, quotation_no: res.data.next_no }));
      }
    } catch (e) {
      console.error("Auto-gen failed");
    }
  };

  const handleCreateNew = () => {
    setBoq(initialBOQ);
    fetchNextQTN();
    setView("create");
  };

  const handleEdit = (quote) => {
    setBoq({
      ...quote,
      overall_transport: quote.transport_charges || 0,
      overall_material_extra: quote.material_extra || 0,
      discount: quote.discount_amount || 0,
      gst_rate: quote.tax_percent || 18,
      taxable: quote.total_amount,
    });
    setView("create");
  };

  const handleDelete = async (id) => {
    if (!window.confirm("Delete this quotation permanently?")) return;
    try {
      await api.post("manage_quotation.php", { action: "delete", id });
      loadInitialData();
    } catch (err) {
      alert("Delete failed");
    }
  };

  const updateItem = (index, field, value) => {
    const newItems = [...boq.items];
    newItems[index][field] = value;
    const base =
      parseFloat(newItems[index].mat_rate || 0) +
      parseFloat(newItems[index].lab_rate || 0);
    const withMargin =
      base + base * (parseFloat(newItems[index].margin || 0) / 100);
    newItems[index].total = withMargin * parseFloat(newItems[index].qty || 0);
    setBoq({ ...boq, items: newItems });
  };

  // Aggregated Math
  const itemTotal = boq.items.reduce(
    (acc, item) => acc + (parseFloat(item.total) || 0),
    0,
  );
  const subtotalBeforeTax =
    itemTotal +
    parseFloat(boq.overall_transport || 0) +
    parseFloat(boq.overall_material_extra || 0);
  const taxable = subtotalBeforeTax - parseFloat(boq.discount || 0);
  const gst = taxable * (parseFloat(boq.gst_rate) / 100);
  const grandTotal = taxable + gst;
  const roundedTotal = Math.round(grandTotal);
  const roundDiff = (roundedTotal - grandTotal).toFixed(2);

  const handleSave = async () => {
    if (!boq.client_name) return alert("Enter Client Name");
    setSaveLoading(true);
    try {
      const payload = {
        ...boq,
        taxable,
        gst,
        grandTotal: roundedTotal,
        action: boq.id ? "update" : "create",
      };
      const res = await api.post("save_quotation.php", payload);
      if (res.data.status === "success") {
        setView("list");
        loadInitialData();
      }
    } catch (err) {
      alert("Save failed");
    } finally {
      setSaveLoading(false);
    }
  };

  const downloadPDF = async (id, qNo) => {
    // We create a promise for the toast to track
    const pdfPromise = new Promise(async (resolve, reject) => {
      try {
        const response = await api.get(`generate_quotation_pdf.php?id=${id}`, {
          responseType: "blob",
        });

        if (response.status === 200) {
          const url = window.URL.createObjectURL(new Blob([response.data]));
          const link = document.createElement("a");
          link.href = url;
          link.setAttribute("download", `QTN_${qNo.replace(/\//g, "_")}.pdf`);
          document.body.appendChild(link);
          link.click();
          link.remove();
          window.URL.revokeObjectURL(url);
          resolve();
        } else {
          reject();
        }
      } catch (err) {
        console.error("PDF Error:", err);
        reject();
      }
    });

    // This triggers the Toast UI
    toast.promise(
      pdfPromise,
      {
        loading: "Generating High-Resolution PDF...",
        success: "Estimate Downloaded Successfully!",
        error: "Could not generate PDF. Please try again.",
      },
      {
        style: {
          minWidth: "300px",
          borderRadius: "16px",
          background: "#1e1b4b",
          color: "#fff",
          fontWeight: "bold",
          fontSize: "12px",
          textTransform: "uppercase",
        },
        success: {
          duration: 4000,
          icon: "📄",
        },
      },
    );
  };

  if (loading)
    return (
      <div className="p-20 text-center font-black animate-pulse text-slate-300 uppercase">
        Syncing Estimates...
      </div>
    );

  return (
    <div className="space-y-8 p-4 bg-slate-50/30 min-h-screen">
      {/* Page Header */}
      <Toaster position="top-right" reverseOrder={false} />
      <div className="flex justify-between items-center">
        <div>
          <h1 className="text-4xl font-black text-slate-900 tracking-tighter italic uppercase">
            Estimate Builder
          </h1>
          <p className="text-slate-400 font-bold text-[10px] uppercase tracking-widest mt-2 italic">
            {`${user.company_name} • Quotation & Project Planning`}
          </p>
        </div>
        <button
          onClick={view === "list" ? handleCreateNew : () => setView("list")}
          className="bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black text-[10px] uppercase shadow-xl hover:bg-slate-900 transition-all flex items-center gap-2"
        >
          {view === "list" ? <Plus size={18} /> : <FileText size={18} />}
          {view === "list" ? "New Quotation" : "Back to List"}
        </button>
      </div>

      {view === "list" ? (
        /* LIST VIEW TABLE */
        <div className="bg-white rounded-[40px] border border-slate-100 shadow-sm overflow-hidden animate-in fade-in duration-500">
          <table className="w-full text-left">
            <thead className="bg-slate-50 text-[10px] font-black text-slate-400 uppercase tracking-widest border-b">
              <tr>
                <th className="p-8">Quote No.</th>
                <th className="p-8">Client Name</th>
                <th className="p-8">Grand Total</th>
                <th className="p-8 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-slate-50">
              {quotations.map((q) => (
                <tr
                  key={q.id}
                  className="hover:bg-slate-50/50 transition-all group"
                >
                  <td className="p-8 font-black text-slate-900 uppercase italic">
                    #{q.quotation_no}
                  </td>
                  <td className="p-8 text-xs font-bold text-slate-500 uppercase tracking-widest">
                    {q.client_name}
                  </td>
                  <td className="p-8 font-black text-lg text-indigo-600">
                    ₹{parseFloat(q.grand_total).toLocaleString()}
                  </td>
                  <td className="p-8 text-right space-x-2">
                    <div className="flex justify-end gap-2 opacity-0 group-hover:opacity-100 transition-opacity">
                      <button
                        onClick={() => setViewingQuote(q)}
                        className="p-3 bg-slate-50 text-slate-400 hover:text-indigo-600 rounded-xl transition-all"
                      >
                        <Eye size={18} />
                      </button>
                      <button
                        onClick={() => handleEdit(q)}
                        className="p-3 bg-slate-50 text-slate-400 hover:text-amber-500 rounded-xl transition-all"
                      >
                        <Edit3 size={18} />
                      </button>
                      <button
                        onClick={() => downloadPDF(q.id, q.quotation_no)}
                        className="p-3 bg-indigo-50 text-indigo-600 rounded-xl transition-all"
                      >
                        <Download size={18} />
                      </button>
                      <button
                        onClick={() => handleDelete(q.id)}
                        className="p-3 bg-rose-50 text-rose-400 rounded-xl transition-all"
                      >
                        <Trash2 size={18} />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        /* CREATE / EDIT FORM */
        <div className="animate-in slide-in-from-bottom-4 duration-500 space-y-8">
          <div className="bg-white rounded-[48px] p-12 shadow-2xl border border-slate-100 space-y-10">
            {/* Metadata Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-8 pb-10 border-b">
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-4 tracking-widest">
                  Quotation ID (Auto)
                </label>
                <input
                  className="w-full p-5 bg-slate-50 rounded-3xl font-black text-indigo-600 outline-none border-2 border-transparent"
                  value={boq.quotation_no}
                  readOnly
                />
              </div>
              <div className="space-y-2">
                <label className="text-[10px] font-black text-slate-400 uppercase ml-4 tracking-widest">
                  Client Name
                </label>
                <div className="relative">
                  <User
                    className="absolute left-5 top-1/2 -translate-y-1/2 text-slate-300"
                    size={18}
                  />
                  <input
                    className="w-full p-5 pl-14 bg-slate-50 rounded-3xl font-bold outline-none border-2 border-transparent focus:border-indigo-100 transition-all"
                    placeholder="Enter Full Name..."
                    value={boq.client_name}
                    onChange={(e) =>
                      setBoq({ ...boq, client_name: e.target.value })
                    }
                  />
                </div>
              </div>
            </div>

            {/* Line Items Builder */}
            <div className="space-y-6">
              <h3 className="text-xs font-black uppercase flex items-center gap-2 px-2 text-indigo-600 tracking-widest">
                <BookOpen size={16} /> Itemized BOQ
              </h3>
              {boq.items.map((item, idx) => (
                <div
                  key={item.id}
                  className="bg-slate-50/50 p-8 rounded-[40px] border border-slate-100 group transition-all hover:bg-white hover:shadow-2xl space-y-4"
                >
                  <div className="grid grid-cols-12 gap-6">
                    <div className="col-span-12 md:col-span-5 space-y-2">
                      <label className="text-[8px] font-black text-slate-400 uppercase ml-2">
                        Description of Work
                      </label>
                      <textarea
                        className="w-full p-4 bg-white rounded-2xl font-bold text-sm h-20 outline-none border border-slate-100 focus:ring-2 ring-indigo-500/10"
                        value={item.description}
                        onChange={(e) =>
                          updateItem(idx, "description", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-span-4 md:col-span-1 space-y-2">
                      <label className="text-[8px] font-black text-slate-400 uppercase ml-2 text-center block">
                        Qty
                      </label>
                      <input
                        type="number"
                        className="w-full p-4 bg-white rounded-2xl font-black text-center"
                        value={item.qty}
                        onChange={(e) => updateItem(idx, "qty", e.target.value)}
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2 space-y-2">
                      <label className="text-[8px] font-black text-slate-400 uppercase ml-2 flex items-center gap-1">
                        <Package size={10} /> Mat. Rate
                      </label>
                      <input
                        type="number"
                        className="w-full p-4 bg-white rounded-2xl font-bold text-indigo-600"
                        value={item.mat_rate}
                        onChange={(e) =>
                          updateItem(idx, "mat_rate", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-span-4 md:col-span-2 space-y-2">
                      <label className="text-[8px] font-black text-slate-400 uppercase ml-2 flex items-center gap-1">
                        <Wrench size={10} /> Lab. Rate
                      </label>
                      <input
                        type="number"
                        className="w-full p-4 bg-white rounded-2xl font-bold"
                        value={item.lab_rate}
                        onChange={(e) =>
                          updateItem(idx, "lab_rate", e.target.value)
                        }
                      />
                    </div>
                    <div className="col-span-12 md:col-span-2 space-y-2">
                      <label className="text-[8px] font-black text-indigo-500 uppercase ml-2">
                        Margin %
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          className="w-full p-4 bg-indigo-50 text-indigo-600 rounded-2xl font-black text-center"
                          value={item.margin}
                          onChange={(e) =>
                            updateItem(idx, "margin", e.target.value)
                          }
                        />
                        <button
                          onClick={() =>
                            setBoq({
                              ...boq,
                              items: boq.items.filter((i) => i.id !== item.id),
                            })
                          }
                          className="p-3 text-rose-300 hover:text-rose-600"
                        >
                          <Trash2 size={18} />
                        </button>
                      </div>
                    </div>
                  </div>
                  <div className="text-right">
                    <p className="text-[10px] font-black text-slate-300 uppercase tracking-widest">
                      Line Item Total:{" "}
                      <span className="text-slate-900 font-black italic">
                        ₹{item.total.toLocaleString()}
                      </span>
                    </p>
                  </div>
                </div>
              ))}
              <button
                onClick={() =>
                  setBoq({
                    ...boq,
                    items: [
                      ...boq.items,
                      {
                        id: Date.now(),
                        description: "",
                        qty: 1,
                        mat_rate: 0,
                        lab_rate: 0,
                        margin: 15,
                        total: 0,
                      },
                    ],
                  })
                }
                className="w-full py-4 border-2 border-dashed border-slate-200 rounded-[30px] text-[10px] font-black uppercase text-slate-400 hover:border-indigo-400 hover:text-indigo-600 transition-all"
              >
                + Add Material Line Item
              </button>
            </div>

            {/* Overheads & Summary Section */}
            <div className="grid grid-cols-1 md:grid-cols-2 gap-10 pt-10 border-t">
              <div className="space-y-6">
                <h3 className="text-xs font-black uppercase text-slate-400 italic px-2 tracking-widest">
                  Project Overheads
                </h3>
                <div className="grid grid-cols-2 gap-6">
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase text-amber-600 ml-4 flex items-center gap-1">
                      <Truck size={12} /> Global Transport
                    </label>
                    <input
                      type="number"
                      className="w-full p-5 bg-slate-50 rounded-3xl font-black"
                      value={boq.overall_transport}
                      onChange={(e) =>
                        setBoq({ ...boq, overall_transport: e.target.value })
                      }
                    />
                  </div>
                  <div className="space-y-2">
                    <label className="text-[9px] font-black uppercase text-indigo-600 ml-4 flex items-center gap-1">
                      <Package size={12} /> Material Surcharge
                    </label>
                    <input
                      type="number"
                      className="w-full p-5 bg-slate-50 rounded-3xl font-black"
                      value={boq.overall_material_extra}
                      onChange={(e) =>
                        setBoq({
                          ...boq,
                          overall_material_extra: e.target.value,
                        })
                      }
                    />
                  </div>
                </div>
                <div className="space-y-2 pt-4">
                  <label className="text-[9px] font-black text-slate-400 uppercase ml-4 tracking-widest">
                    Adjustment / Discount (₹)
                  </label>
                  <input
                    type="number"
                    className="w-full p-5 bg-slate-50 rounded-3xl font-black text-rose-500"
                    value={boq.discount}
                    onChange={(e) =>
                      setBoq({ ...boq, discount: e.target.value })
                    }
                  />
                </div>
              </div>

              <div className="bg-slate-900 text-white p-10 rounded-[48px] text-right shadow-2xl flex flex-col justify-center border border-white/5">
                <p className="text-slate-500 font-bold uppercase text-[10px] tracking-[0.3em] mb-2">
                  Final Proposal Total
                </p>
                <h3 className="text-7xl font-black italic tracking-tighter">
                  ₹{roundedTotal.toLocaleString()}
                </h3>
                <p
                  className={`text-[10px] font-black uppercase mt-2 tracking-widest ${parseFloat(roundDiff) >= 0 ? "text-emerald-400" : "text-rose-400"}`}
                >
                  {parseFloat(roundDiff) >= 0 ? "+" : ""}
                  {roundDiff} Round Off
                </p>
                <div className="flex justify-end gap-6 pt-6 text-[10px] font-black uppercase tracking-widest text-slate-400 border-t border-white/10 mt-6">
                  <span>Taxable: ₹{taxable.toLocaleString()}</span>
                  <span className="text-indigo-400">
                    GST ({boq.gst_rate}%): ₹{gst.toLocaleString()}
                  </span>
                </div>
              </div>
            </div>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-6 pt-4">
              <button
                disabled={saveLoading}
                onClick={() => handleSave()}
                className="py-7 bg-indigo-600 text-white rounded-[32px] font-black text-xl shadow-2xl hover:bg-slate-900 transition-all flex justify-center items-center gap-3 italic uppercase tracking-tighter"
              >
                {saveLoading ? (
                  <Loader2 className="animate-spin" />
                ) : (
                  <Save size={24} />
                )}
                {boq.id
                  ? "Update Proposal Record"
                  : "Save & Finalize Quotation"}
              </button>
              {boq.id && (
                <button
                  onClick={() => downloadPDF(boq.id, boq.quotation_no)}
                  className="py-7 bg-slate-900 text-white rounded-[32px] font-black text-xl shadow-2xl hover:bg-indigo-600 transition-all flex justify-center items-center gap-3 uppercase tracking-tighter"
                >
                  <Download size={24} /> Download Current PDF
                </button>
              )}
            </div>
          </div>
        </div>
      )}

      {/* READ-ONLY VIEW MODAL */}
      {viewingQuote && (
        <div className="fixed inset-0 z-[600] bg-slate-900/95 backdrop-blur-xl flex items-center justify-center p-4">
          <div className="bg-white w-full max-w-4xl rounded-[50px] shadow-2xl overflow-hidden flex flex-col max-h-[90vh] animate-in zoom-in-95 duration-200">
            <div className="p-10 border-b flex justify-between items-center">
              <div>
                <h2 className="text-4xl font-black italic uppercase tracking-tighter">
                  #{viewingQuote.quotation_no}
                </h2>
                <p className="text-indigo-600 font-bold text-xs uppercase tracking-[0.2em] mt-2">
                  Client: {viewingQuote.client_name}
                </p>
              </div>
              <button
                onClick={() => setViewingQuote(null)}
                className="p-4 bg-slate-50 rounded-full text-rose-500 hover:bg-rose-50 transition-colors"
              >
                <X size={24} />
              </button>
            </div>

            <div className="p-10 overflow-y-auto flex-1 space-y-8">
              <table className="w-full text-left">
                <thead className="text-[10px] font-black uppercase text-slate-400 border-b pb-4">
                  <tr>
                    <th className="pb-4">Work Description</th>
                    <th className="pb-4 text-right">Aggregate Total</th>
                  </tr>
                </thead>
                <tbody className="divide-y divide-slate-50">
                  {viewingQuote.items?.map((item, i) => (
                    <tr key={i}>
                      <td className="py-6 font-bold text-slate-700 text-sm italic">
                        {item.description}{" "}
                        <span className="text-[10px] text-slate-400 not-italic ml-2">
                          x {item.qty} qty
                        </span>
                      </td>
                      <td className="py-6 text-right font-black text-slate-900">
                        ₹{parseFloat(item.total).toLocaleString()}
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>

              <div className="grid grid-cols-2 gap-8 bg-slate-50 p-8 rounded-[32px] border border-slate-100">
                <div className="text-[10px] font-black text-slate-400 uppercase space-y-2">
                  <div className="flex justify-between">
                    <span>Transport:</span>
                    <span className="text-slate-900">
                      ₹
                      {parseFloat(
                        viewingQuote.transport_charges,
                      ).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between">
                    <span>Material Extra:</span>
                    <span className="text-slate-900">
                      ₹
                      {parseFloat(viewingQuote.material_extra).toLocaleString()}
                    </span>
                  </div>
                  <div className="flex justify-between text-rose-500">
                    <span>Discount:</span>
                    <span>
                      - ₹
                      {parseFloat(
                        viewingQuote.discount_amount,
                      ).toLocaleString()}
                    </span>
                  </div>
                </div>
                <div className="text-right border-l pl-8 flex flex-col justify-center">
                  <p className="text-[10px] font-black text-slate-400 uppercase tracking-widest">
                    Grand Total
                  </p>
                  <h4 className="text-4xl font-black italic text-indigo-600">
                    ₹{parseFloat(viewingQuote.grand_total).toLocaleString()}
                  </h4>
                </div>
              </div>
            </div>

            <div className="p-10 bg-slate-900 flex justify-between items-center">
              <button
                onClick={() =>
                  downloadPDF(viewingQuote.id, viewingQuote.quotation_no)
                }
                className="flex items-center gap-3 bg-indigo-600 text-white px-8 py-4 rounded-2xl font-black uppercase text-xs italic hover:bg-white hover:text-slate-900 transition-all shadow-lg"
              >
                <Download size={20} /> Generate & Download Signed PDF
              </button>
              <div className="text-right text-white/40 text-[9px] font-black uppercase tracking-[0.3em]">
                {user.company_name} <br /> Professional BOQ System
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default QuotationBOQ;
